/* eslint-disable tsdoc/syntax */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable tsdoc/syntax */
import React, { useEffect, useRef, useState, memo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FormGroup } from "@mui/material";
import {
  SelectComponent,
  TextFieldComponent,
  PinInputComponent,
  ModalComponent,
  ButtonComponent,
} from "@zenith/components";
import { SelectItemsType } from "@zenith/components/ui/SelectComponent/select-component/interfaces";
import {
  PIN_LENGTH,
  DEFAULT_CHANNEL,
  ACCESS,
  REFRESH,
  GH,
  NG,
  NUMBER_INPUT_REGEX,
  BLOCKED_USER_MESSAGE,
  SUCCESS_API_CODE,
} from "@zenith/data/constants";
import {
  setPhoneNumberValidations,
  setPinValidations,
} from "@zenith/features/Authentication/validations/AuthValidations";
import { CheckBox } from "@zenith/components/ui/CheckBoxComponent/checkbox-component";
import { getPhoneNumberLength } from "@zenith/features/globalUtils";
import { AppNavigation } from "@zenith/hooks";
import {
  SIGN_UP,
  FORGOT_PIN,
  DASHBOARD,
  SELECT_PROFILE,
  SIGN_IN_TWO_FACTOR,
} from "@zenith/routes/paths";
import { handleTokenStore } from "@zenith/services/token";
import { captureMessage, withScope } from "@zenith/utils/sentry";
import {
  ResourceApiService,
  ApiResponseDTOListCountrySummarizedDTO,
  CountrySummarizedDTO,
  UtilityApiService,
  AuthenticationApiService,
  SignInResponseDTO,
  ApiResponseDTO,
} from "@zenith/services/user";
import { getCountryFlag, handleCountryCode, encrypt } from "@zenith/utils";
import { SignInDataType, SignInErrorsTypes } from "./interfaces";
import Cookies from "js-cookie";
import {
  useGlobalContextActions,
  useMobileVerificationContext,
  useSignUpContext,
} from "@zenith/context/global-context/hooks";
import { requestForToken } from "@zenith/firebase";
import { useNotificationContext } from "@zenith/context";
import ToastSnackbarComponent from "@zenith/components/ui/ToastSnackbarComponent/toast-snackbar-component/toast-snackbar-component";
import { useBusinessRegistrationStatus } from "@zenith/hooks/useBusinessRegistrationStatus";
import {
  rememberReturnTo,
  takeReturnTo,
} from "@zenith/features/Authentication/utils/pendingReturnTo";
import WalletIcon from "@zenith/assets/icons/WalletIcon";
import { Logo } from "@zenith/shared";
import { loadEnv } from "vite";

type RememberState = "unchecked" | "checked" | "indeterminate";

const SignInPage = memo(function SignInPage() {
  const xApiKey = import.meta.env.VITE_X_API_KEY;

  const { navigateTo } = AppNavigation();
  const location = useLocation();

  // Park the destination a caller asked for (the invitation page does this) so
  // it survives the 2FA and profile-selection screens that may follow.
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  useEffect(() => {
    rememberReturnTo(returnTo);
  }, [returnTo]);

  const { setSignUpContext } = useSignUpContext();
  const { clearAllContexts } = useGlobalContextActions();
  const { getBusinessRegistrationType } = useBusinessRegistrationStatus();
  const { setOpen } = useNotificationContext();
  const { setPhoneNumber, setCountryCode } = useMobileVerificationContext();
  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  const [countries, setCountries] = useState<SelectItemsType[]>([]),
    [isCountriesLoading, setIsCountriesLoading] = useState<boolean>(false);

  const [fcmToken, setFcmToken] = useState<string | null>(null);

  useEffect(() => {
    requestForToken()
      .then((token) => {
        if (token) {
          setFcmToken(token);
        }
      })
      .catch((error) => {
        console.error("Error getting FCM token:", error);
      });
  }, []);

  useEffect(() => {
    sessionStorage.removeItem(REFRESH);
    sessionStorage.removeItem(ACCESS);
    clearAllContexts();
    setIsCountriesLoading(true);

    ResourceApiService.getCountries()
      .then((res: ApiResponseDTOListCountrySummarizedDTO) => {
        if (!res.data) return;
        const items = res.data.map((country: CountrySummarizedDTO) => ({
          value: country.countryCode as string,
          label: country.countryCallingCode as string,
          icon: getCountryFlag(country.countryCode as string),
          countryName: country.countryName,
        }));

        setCountries(items);

        if (items.length > 0) {
          setFormData((prevFormData) => ({
            ...prevFormData,
            countryCode: items[0].value,
          }));
        }
      })
      .catch((error) => {
        throw new Error(error);
      })
      .finally(() => setIsCountriesLoading(false));
  }, []);

  const [isLoading, setIsLoading] = useState<boolean>(false),
    initialFormErrorState = {
      countryCode: {
        message: "",
        hasError: false,
      },
      phoneNumber: {
        message: "",
        hasError: false,
      },
      pin: {
        message: "",
        hasError: false,
      },
    },
    [formData, setFormData] = useState<SignInDataType>({
      countryCode: sessionStorage.getItem("countryCode") || "",
      phoneNumber: sessionStorage.getItem("phoneNumber") || "",
      pin: "",
    }),
    [formErrors, setFormErrors] = useState<SignInErrorsTypes>(
      initialFormErrorState
    ),
    [newPin, setNewPin] = useState<Array<number | string>>(
      new Array(PIN_LENGTH).fill("")
    ),
    [showLockedPopup, setShowLockedPopup] = useState<boolean>(false);

  const formRef = useRef<HTMLFormElement>(null);

  const [rememberMe, setRememberMe] = useState<RememberState>(() => {
    const stored = sessionStorage.getItem("phoneNumber");

    if (stored) return "checked";

    return "unchecked";
  });

  // Submit form on Enter when focus has left all inputs (e.g. after last PIN digit auto-blurs)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || isLoading) return;
      if (formRef.current?.contains(document.activeElement)) return;
      formRef.current?.requestSubmit();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isLoading]);

  function onCountryCodeChange(
    event: React.SyntheticEvent | null,
    newValue: string | null
  ): void {
    if (event && newValue) {
      setFormData({
        ...formData,
        phoneNumber: "",
        countryCode: newValue,
      });
    }
  }

  function onPhoneNumberChange(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    setFormErrors({
      ...formErrors,
      phoneNumber: {
        message: "",
        hasError: false,
      },
    });
    setFormData({
      ...formData,
      phoneNumber: event.target.value.replace(NUMBER_INPUT_REGEX, "") as string,
    });
  }

  function onPhoneNumberBlur(): void {
    setFormErrors({
      ...formErrors,
      phoneNumber: {
        ...setPhoneNumberValidations(
          formData.phoneNumber,
          getPhoneNumberLength(formData.countryCode, formData.phoneNumber)
        ),
      },
    });
  }

  function onPinChanged(pinEntry: number | string, index: number): void {
    const pin = [...newPin];
    pin[index] = pinEntry;
    setNewPin(pin);
    setFormData({ ...formData, pin: pin.join("") });
    setFormErrors({
      ...formErrors,
      pin: {
        message: "",
        hasError: false,
      },
    });
  }

  function onPinBlur(): void {
    setFormErrors({
      ...formErrors,
      pin: {
        ...setPinValidations(formData.pin),
      },
    });
  }

  function handleErrorMessage(error: any): string {
    const errorMessage =
      error.body?.message || error.message || "An error occurred";

    if (errorMessage.toLowerCase().includes("passcode")) {
      return "Invalid Passcode. Please try again.";
    }

    if (errorMessage.toLowerCase().includes("phone number")) {
      return "Invalid phone number. Please check and try again.";
    }

    if (error.body?.internalCode === "ZENZ-CROSS-004") {
      return "Invalid credentials. Please try again.";
    }

    if (error.body?.internalCode === "ZENZ-USER-043") {
      return BLOCKED_USER_MESSAGE;
    }

    if (error.body?.internalCode === "ZENZ-USER-004") {
      setShowLockedPopup(true);
      return "Your account is locked. Please contact support.";
    }

    return errorMessage;
  }

  function handleRegister(): void {
    navigateTo(SIGN_UP);
  }

  function handleForgot(): void {
    navigateTo(FORGOT_PIN);
  }

  function handleClosePopup(): void {
    setShowLockedPopup(false);
  }

  function disableSignIn(): boolean {
    if (!formData.countryCode || Number(formData.pin.length) < PIN_LENGTH)
      return true;
    if (
      formData.countryCode === NG &&
      formData.phoneNumber.length <
        getPhoneNumberLength(NG, formData.phoneNumber)
    ) {
      return true;
    } else if (
      formData.countryCode === GH &&
      formData.phoneNumber.length <
        getPhoneNumberLength(GH, formData.phoneNumber)
    ) {
      return true;
    }
    return false;
  }

  async function handleSignIn(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // Clear previous errors
    setFormErrors(initialFormErrorState);

    // Validate phone number
    if (!formData.phoneNumber.length) {
      setFormErrors({
        ...formErrors,
        phoneNumber: {
          message: "Please enter your phone number",
          hasError: true,
        },
      });
      return;
    }

    // Validate PIN
    if (!formData.pin.length) {
      setFormErrors({
        ...formErrors,
        pin: {
          message: "Passcode code must have six (6) digits",
          hasError: true,
        },
      });
      return;
    }

    setIsLoading(true);
    const numberCountryCode = handleCountryCode(formData.countryCode);

    try {
      // Get public key for encryption
      const publicKeyResponse = await UtilityApiService.getPublicKey(
        DEFAULT_CHANNEL,
        xApiKey
      );

      const challenge = publicKeyResponse?.data?.challenge as string;
      if (!publicKeyResponse.data || !publicKeyResponse.success) {
        throw new Error("Failed to get encryption key");
      }

      const publicKeyBase64 = publicKeyResponse.data.publicKeyBase64 || "";

      // Try encryption with different algorithms
      const encryptedPin = await tryEncryptionMethods(publicKeyBase64);
      if (!encryptedPin) {
        throw new Error("Failed to encrypt Passcode");
      }

      // Encrypt FCM token if available
      let encryptedFcmToken = null;
      if (fcmToken) {
        encryptedFcmToken = encrypt(fcmToken, publicKeyBase64, "sha256");
      }

      // Proceed with sign in
      const signInResponse = await AuthenticationApiService.signIn(
        DEFAULT_CHANNEL,
        xApiKey,
        {
          challenge,
          countryCode: formData.countryCode,
          passcode: encryptedPin,
          phoneNumber: numberCountryCode + formData.phoneNumber,
        },
        encryptedFcmToken || undefined
      );

      handleSuccessfulSignIn(signInResponse, numberCountryCode);
    } catch (error) {
      handleSignInError(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function tryEncryptionMethods(
    publicKeyBase64: string
  ): Promise<string | null> {
    const algorithms: Array<"sha256" | "sha1" | "pkcs1"> = [
      "sha256",
      "sha1",
      "pkcs1",
    ];

    for (const algorithm of algorithms) {
      try {
        const encryptedPin = encrypt(formData.pin, publicKeyBase64, algorithm);
        if (encryptedPin) {
          return encryptedPin;
        }
      } catch (err) {
        console.warn(`Encryption failed with ${algorithm}`, err);
      }
    }

    return null;
  }

  function handleSuccessfulSignIn(
    response: SignInResponseDTO | ApiResponseDTO,
    numberCountryCode: string
  ) {
    if (rememberMe) {
      sessionStorage.setItem("phoneNumber", formData.phoneNumber);
      sessionStorage.setItem("countryCode", formData.countryCode);
    } else {
      sessionStorage.removeItem("phoneNumber");
      sessionStorage.removeItem("countryCode");
    }
    const phoneNumber = numberCountryCode + formData.phoneNumber;

    if ("data" in response && response.data) {
      if (response.data.verification2fa) {
        navigateTo(SIGN_IN_TWO_FACTOR, {
          state: { phoneNumber },
        });
      } else if (response.data.userExternalId) {
        const { userExternalId } = response.data;
        setSignUpContext({ userExternalId });
        setPhoneNumber(phoneNumber);
        setCountryCode(formData.countryCode);
        getBusinessRegistrationType(userExternalId);
      } else {
        handleTokenStore(
          ACCESS,
          response.data.accessToken,
          response.data.expiresIn
        );
        handleTokenStore(
          REFRESH,
          response.data.refreshToken,
          response.data.refreshTokenExpiresIn
        );
        Cookies.set(ACCESS, response.data.accessToken, {
          expires: response.data.expiresIn,
        });

        // The sign-in response states whether a profile has to be chosen:
        // POS sessions get false (the backend has already tied them to the
        // terminal's business and outlet), multi-profile web sessions get
        // true. SignInResponseDTO now declares profileSelectionRequired and
        // profiles directly, so no cast or extra profiles() round trip is
        // needed to read them.
        if (response.data.profileSelectionRequired) {
          navigateTo(SELECT_PROFILE, {
            state: { profiles: response.data.profiles },
          });
        } else {
          // An invitee sent here mid-task (an invitation link) must go back to
          // it, not to the dashboard - the invitation is what accepts the
          // membership, and it is never reached otherwise.
          navigateTo(takeReturnTo() ?? DASHBOARD);
        }
      }
    } else {
      throw new Error("Unexpected response structure");
    }
  }

  /**
   * A refused sign-in reaches Sentry because the API layer cannot describe it —
   * a wrong PIN is answered with a business code, not a failed request. Neither
   * the phone number nor the PIN is attached; the status and the code are enough
   * to triage, and a spike here is what says customers cannot get in.
   */
  function reportSignInFailure(error: unknown) {
    const failure = (error ?? {}) as {
      status?: number;
      statusCode?: number;
      body?: { internalCode?: string };
    };
    const status = Number(failure.status ?? failure.statusCode);
    const internalCode = failure.body?.internalCode;

    withScope((scope) => {
      scope.setTag("source", "login");
      scope.setTag(
        "login.status",
        Number.isFinite(status) ? String(status) : "network"
      );
      if (internalCode) scope.setTag("internalCode", String(internalCode));
      scope.setExtra("reason", handleErrorMessage(error));

      // A lockout, or a fault on our side, is worth waking someone for; a wrong
      // PIN is not.
      const severe =
        !Number.isFinite(status) || status === 429 || status >= 500;
      captureMessage("Sign-in failed", severe ? "error" : "warning");
    });
  }

  function handleSignInError(error: any) {
    console.error("Sign-in error:", error);
    reportSignInFailure(error);

    if (error.status === 429 || error.statusCode === 429) {
      setFormErrors({
        ...formErrors,
        pin: {
          message:
            "You've made too many requests in a short time. Please wait a moment and try again.",
          hasError: true,
        },
      });
      return;
    }

    const errorMessage = handleErrorMessage(error);
    const field = errorMessage.toLowerCase().includes("phone number")
      ? "phoneNumber"
      : "pin";

    setFormErrors({
      ...formErrors,
      [field]: {
        message: errorMessage,
        hasError: true,
      },
    });

    // Show toaster for important messages
    if (
      error.body?.internalCode === "ZENZ-USER-043" ||
      error.body?.internalCode === "ZENZ-USER-004"
    ) {
      setToasterData({
        title: "Account Issue",
        message: errorMessage,
        status: "error",
      });
      setOpen(true);
    }
  }

  return (
    <div className="mx-3 my-10 flex h-screen w-full flex-1 flex-col items-center justify-center overflow-x-hidden md:mx-auto">
      {toasterData.message && toasterData.title && (
        <ToastSnackbarComponent
          horizontal="center"
          status={toasterData.status}
          vertical="top"
          hasAutoHideDuration
          size="lg"
          title={toasterData.title}
          text={toasterData.message}
        />
      )}

      {showLockedPopup && (
        <ModalComponent
          showModal={showLockedPopup}
          title="Forgot Your Passcode?"
          body="Looks like you've forgotten your Passcode."
          secondLine="Click the button below to start the reset process."
          buttonText="Okay"
          onClickClose={handleClosePopup}
          onClickButton={handleForgot}
        />
      )}
      <div className="absolute top-5 flex gap-2 lg:hidden">
        <WalletIcon height={40} width={40} className="my-auto" />
        <Logo />
      </div>
      <div className="flex flex-col items-center justify-center">
        <h5 className="mb-4 text-4.5xl font-medium">Welcome</h5>
        <span className="text-center text-xl font-normal text-neutral-600">
          Access your already existing Finopay account
        </span>
      </div>
      <div className="flex w-full max-w-md flex-col items-center md:w-1/3 lg:w-2/3 xl:w-1/2 xs:w-11/12">
        <form
          ref={formRef}
          onSubmit={handleSignIn}
          className="w-full"
          noValidate
        >
          <div className="mt-16 flex w-full flex-row gap-x-4">
            <SelectComponent
              id="country-code-dropdown"
              label="Country"
              labelId="country-code-dropdown-label"
              className=""
              defaultValue=""
              value={formData.countryCode}
              onChange={onCountryCodeChange}
              items={countries}
              isLoading={isCountriesLoading}
              editable={false}
            />
            <div className="flex w-full grow flex-col">
              <TextFieldComponent
                id="phone-number-field"
                labelId="phone-number-field-label"
                className="w-5/6 xsm:w-full"
                containerClassName="flex grow"
                hasError={formErrors.phoneNumber.hasError}
                label="Phone number"
                placeholder="Enter phone number"
                maxLength={getPhoneNumberLength(
                  formData.countryCode,
                  formData.phoneNumber
                )}
                minLength={getPhoneNumberLength(
                  formData.countryCode,
                  formData.phoneNumber
                )}
                type="tel"
                value={formData.phoneNumber}
                onChange={onPhoneNumberChange}
                onBlur={onPhoneNumberBlur}
              />
              {formErrors.phoneNumber.hasError && (
                <div className="mt-1 ml-2 text-red-400">
                  <span className="break-words">
                    {formErrors.phoneNumber.message}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="mx-auto mt-8 w-full">
            <PinInputComponent
              id="sign-in-pin"
              label="Enter the six (6) digit Passcode"
              labelClassName="ml-2 mb-3 text-neutral-500 text-sm"
              pinLength={PIN_LENGTH}
              pin={newPin}
              type={"tel"}
              pinContainerClass="gap-2 md:gap-7"
              masked={true}
              onPinChanged={onPinChanged}
              onBlur={onPinBlur}
              hasError={formErrors.pin.hasError}
              isValidating={false}
              validationMessage={formErrors.pin.message}
            />
          </div>
          <div className="mt-3 flex items-center justify-between font-normal text-neutral-600">
            <FormGroup>
              <CheckBox
                label="Remember me"
                state={rememberMe}
                onChange={setRememberMe}
              />
            </FormGroup>
            <NavLink to="#" onClick={handleForgot}>
              <span className="text-neutral-600">Forgot Passcode?</span>
            </NavLink>
          </div>
          <div className="mt-16 flex flex-col items-center">
            <ButtonComponent
              label="Sign In"
              fullWidth
              onClick={handleSignIn}
              disabled={disableSignIn() || isLoading}
              isLoading={isLoading}
              size="large"
              type="primary"
              id="sign-in-button"
            />
          </div>
        </form>
        <div className="text-main-200-100 mt-8 flex gap-1 font-normal">
          <span>Don't have an account? </span>
          <ButtonComponent
            label="Sign Up"
            onClick={handleRegister}
            id="sign-up"
            size="large"
            type="tertiary"
          />
        </div>
      </div>
    </div>
  );
});

export default SignInPage;
